/**
 * Staff Performance API Service
 * ==============================
 * Handles all API calls for staff task management, development plans,
 * notification preferences, metrics, gamification, task templates,
 * bulk import, activity feed, and onboarding.
 *
 * Migrated to central `apiClient` (cookie auth, CSRF, JSON parsing,
 * 401 redirect) — May 2026.
 */
import { apiClient } from '@/lib/apiClient';
import { logIfReal } from '@/utils/transientError';

const BASE = '/api/staff-performance';

// ============================================================================
// TASK MANAGEMENT
// ============================================================================

export const getTasks = ({ staffId, status, priority, limit = 50 } = {}) => {
  const params = new URLSearchParams();
  if (staffId) params.append('staff_id', staffId);
  if (status) params.append('status', status);
  if (priority) params.append('priority', priority);
  if (limit) params.append('limit', limit);
  const qs = params.toString();
  return apiClient.get(`${BASE}/tasks${qs ? '?' + qs : ''}`);
};

export const createTask = (task) => apiClient.post(`${BASE}/tasks`, task);

export const updateTask = (taskId, updates) =>
  apiClient.put(`${BASE}/tasks/${taskId}`, updates);

export const deleteTask = (taskId) => apiClient.delete(`${BASE}/tasks/${taskId}`);

export const bulkAssignTasks = (staffIds, taskTemplate) =>
  apiClient.post(`${BASE}/tasks/bulk-assign`, {
    staff_ids: staffIds,
    task_template: taskTemplate,
  });

// ============================================================================
// DEVELOPMENT PLANS
// ============================================================================

export const getDevelopmentPlans = (staffId = null) => {
  const url = staffId
    ? `${BASE}/development-plans?staff_id=${staffId}`
    : `${BASE}/development-plans`;
  return apiClient.get(url);
};

export const createDevelopmentPlan = (plan) =>
  apiClient.post(`${BASE}/development-plans`, plan);

export const updateMilestone = (planId, milestoneId, completed, notes = null) =>
  apiClient.put(`${BASE}/development-plans/${planId}/milestone`, {
    milestone_id: milestoneId,
    completed,
    notes,
    completed_at: completed ? new Date().toISOString() : null,
  });

// ============================================================================
// NOTIFICATION PREFERENCES
// ============================================================================

export const getNotificationPreferences = () =>
  apiClient.get(`${BASE}/notification-preferences`);

export const saveNotificationPreferences = (preferences) =>
  apiClient.post(`${BASE}/notification-preferences`, preferences);

// ============================================================================
// METRICS
// ============================================================================

export const getMetricsSummary = (period = 'month') =>
  apiClient.get(`${BASE}/metrics/summary?period=${period}`);

export const getMetricsByStaff = () => apiClient.get(`${BASE}/metrics/by-staff`);

// ============================================================================
// GAMIFICATION
// ============================================================================

export const getLeaderboard = (period = 'week', limit = 10) =>
  apiClient.get(`${BASE}/gamification/leaderboard?period=${period}&limit=${limit}`);

export const getStaffBadges = (staffId) =>
  apiClient.get(`${BASE}/gamification/badges/${staffId}`);

export const awardBadge = (staffId, badgeId) =>
  apiClient.post(`${BASE}/gamification/award-badge?staff_id=${staffId}&badge_id=${badgeId}`);

// ============================================================================
// TASK TEMPLATES
// ============================================================================

export const getTaskTemplates = (category = null) => {
  const url = category
    ? `${BASE}/task-templates?category=${category}`
    : `${BASE}/task-templates`;
  return apiClient.get(url);
};

export const createTaskTemplate = (template) =>
  apiClient.post(`${BASE}/task-templates`, template);

export const assignFromTemplate = (templateId, staffIds, dueDate = null) => {
  const params = new URLSearchParams();
  if (dueDate) params.append('due_date', dueDate);
  return apiClient.post(
    `${BASE}/task-templates/${templateId}/assign?${params.toString()}`,
    staffIds,
  );
};

export const deleteTaskTemplate = (templateId) =>
  apiClient.delete(`${BASE}/task-templates/${templateId}`);

// ============================================================================
// BULK IMPORT
// ============================================================================

export const bulkImportStaff = (staffList) =>
  apiClient.post(`${BASE}/staff-portal/bulk-import`, staffList);

export const getImportTemplate = () =>
  apiClient.get(`${BASE}/staff-portal/export-template`);

// ============================================================================
// REAL-TIME NOTIFICATIONS (polling-based pseudo-WebSocket)
// ============================================================================

let notificationPollingInterval = null;
let notificationCallbacks = [];

export const subscribeToTaskNotifications = (callback) => {
  notificationCallbacks.push(callback);

  if (!notificationPollingInterval) {
    let lastTaskCount = 0;

    notificationPollingInterval = setInterval(async () => {
      try {
        const data = await getTasks({ limit: 10 });
        const newTaskCount = data.summary?.total || 0;

        if (newTaskCount > lastTaskCount && lastTaskCount > 0) {
          const newTasks = data.tasks.slice(0, newTaskCount - lastTaskCount);
          notificationCallbacks.forEach((cb) => cb(newTasks, 'new_tasks'));
        }
        lastTaskCount = newTaskCount;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Task notification polling error:', error);
      }
    }, 30000);
  }

  return () => {
    notificationCallbacks = notificationCallbacks.filter((cb) => cb !== callback);
    if (notificationCallbacks.length === 0 && notificationPollingInterval) {
      clearInterval(notificationPollingInterval);
      notificationPollingInterval = null;
    }
  };
};

export const pushTaskNotification = (task, action = 'created') => {
  notificationCallbacks.forEach((cb) => cb([task], action));
};

// ============================================================================
// ACTIVITY FEED
// ============================================================================

export const getActivityFeed = async ({ limit = 20, offset = 0, types = null } = {}) => {
  try {
    let url = `${BASE}/activity-feed?limit=${limit}&offset=${offset}`;
    if (types) url += `&types=${types}`;
    return await apiClient.get(url);
  } catch (error) {
    logIfReal('Failed to fetch activity feed', error);
    return { success: false, activities: [], total: 0, unread_count: 0 };
  }
};

export const markActivitiesRead = async (activityIds) => {
  try {
    return await apiClient.post(`${BASE}/activity-feed/mark-read`, activityIds);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to mark activities as read:', error);
    return { success: false };
  }
};

// ============================================================================
// ONBOARDING WITH DEVELOPMENT PLANS
// ============================================================================

export const bulkImportWithOnboarding = (staffList, onboardingConfig = {}) =>
  apiClient.post(`${BASE}/staff-portal/bulk-import-with-onboarding`, {
    staff_list: staffList,
    onboarding: {
      create_development_plan: onboardingConfig.createDevelopmentPlan !== false,
      plan_type: onboardingConfig.planType || '90_day',
      assign_template_ids: onboardingConfig.templateIds || [],
      send_welcome_notification: onboardingConfig.sendWelcomeNotification !== false,
    },
  });

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Tasks
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  bulkAssignTasks,
  // Development Plans
  getDevelopmentPlans,
  createDevelopmentPlan,
  updateMilestone,
  // Notification Preferences
  getNotificationPreferences,
  saveNotificationPreferences,
  // Metrics
  getMetricsSummary,
  getMetricsByStaff,
  // Gamification
  getLeaderboard,
  getStaffBadges,
  awardBadge,
  // Task Templates
  getTaskTemplates,
  createTaskTemplate,
  assignFromTemplate,
  deleteTaskTemplate,
  // Bulk Import
  bulkImportStaff,
  getImportTemplate,
  // Real-time
  subscribeToTaskNotifications,
  pushTaskNotification,
  // Activity Feed
  getActivityFeed,
  markActivitiesRead,
  // Onboarding
  bulkImportWithOnboarding,
};
